import { useCallback, useMemo } from "react";

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface UseFieldAlignmentOptions {
  /** Return the rect for a given field ID, or null if not found. */
  getFieldRect: (fieldId: string) => Rect | null;
  /** Called when fields should move to new rects. */
  onMoveFields: (updates: Array<{ id: string; rect: Rect }>) => void;
  /** Set of currently selected field IDs. */
  selectedFieldIds: Set<string>;
}

interface UseFieldAlignmentResult {
  /** Whether alignment operations are available (2+ selected fields). */
  canAlign: boolean;
  /** Align all selected fields to the left edge of the leftmost field. */
  alignLeft: () => void;
  /** Align all selected fields to the right edge of the rightmost field. */
  alignRight: () => void;
  /** Align all selected fields to the top edge of the topmost field. */
  alignTop: () => void;
  /** Align all selected fields to the bottom edge of the bottommost field. */
  alignBottom: () => void;
  /** Align all selected fields to the horizontal center of the group. */
  alignCenterH: () => void;
  /** Align all selected fields to the vertical center of the group. */
  alignCenterV: () => void;
  /** Distribute selected fields evenly horizontally. */
  distributeH: () => void;
  /** Distribute selected fields evenly vertically. */
  distributeV: () => void;
}

export function useFieldAlignment({
  getFieldRect,
  onMoveFields,
  selectedFieldIds,
}: UseFieldAlignmentOptions): UseFieldAlignmentResult {
  const canAlign = selectedFieldIds.size >= 2;

  const getSelectedRects = useCallback(() => {
    const rects: Array<{ id: string; rect: Rect }> = [];
    selectedFieldIds.forEach((id) => {
      const rect = getFieldRect(id);
      if (rect) rects.push({ id, rect });
    });
    return rects;
  }, [selectedFieldIds, getFieldRect]);

  const alignLeft = useCallback(() => {
    const items = getSelectedRects();
    if (items.length < 2) return;
    const minX = Math.min(...items.map((i) => i.rect.x));
    onMoveFields(items.map((i) => ({ id: i.id, rect: { ...i.rect, x: minX } })));
  }, [getSelectedRects, onMoveFields]);

  const alignRight = useCallback(() => {
    const items = getSelectedRects();
    if (items.length < 2) return;
    const maxRight = Math.max(...items.map((i) => i.rect.x + i.rect.width));
    onMoveFields(items.map((i) => ({ id: i.id, rect: { ...i.rect, x: maxRight - i.rect.width } })));
  }, [getSelectedRects, onMoveFields]);

  const alignTop = useCallback(() => {
    const items = getSelectedRects();
    if (items.length < 2) return;
    const minY = Math.min(...items.map((i) => i.rect.y));
    onMoveFields(items.map((i) => ({ id: i.id, rect: { ...i.rect, y: minY } })));
  }, [getSelectedRects, onMoveFields]);

  const alignBottom = useCallback(() => {
    const items = getSelectedRects();
    if (items.length < 2) return;
    const maxBottom = Math.max(...items.map((i) => i.rect.y + i.rect.height));
    onMoveFields(items.map((i) => ({ id: i.id, rect: { ...i.rect, y: maxBottom - i.rect.height } })));
  }, [getSelectedRects, onMoveFields]);

  const alignCenterH = useCallback(() => {
    const items = getSelectedRects();
    if (items.length < 2) return;
    const centers = items.map((i) => i.rect.x + i.rect.width / 2);
    const avgCenter = centers.reduce((a, b) => a + b, 0) / centers.length;
    onMoveFields(items.map((i) => ({ id: i.id, rect: { ...i.rect, x: avgCenter - i.rect.width / 2 } })));
  }, [getSelectedRects, onMoveFields]);

  const alignCenterV = useCallback(() => {
    const items = getSelectedRects();
    if (items.length < 2) return;
    const centers = items.map((i) => i.rect.y + i.rect.height / 2);
    const avgCenter = centers.reduce((a, b) => a + b, 0) / centers.length;
    onMoveFields(items.map((i) => ({ id: i.id, rect: { ...i.rect, y: avgCenter - i.rect.height / 2 } })));
  }, [getSelectedRects, onMoveFields]);

  const distributeH = useCallback(() => {
    const items = getSelectedRects();
    if (items.length < 3) return;
    const sorted = [...items].sort((a, b) => a.rect.x - b.rect.x);
    const first = sorted[0].rect.x;
    const last = sorted[sorted.length - 1].rect.x;
    const step = (last - first) / (sorted.length - 1);
    onMoveFields(sorted.map((item, i) => ({ id: item.id, rect: { ...item.rect, x: first + step * i } })));
  }, [getSelectedRects, onMoveFields]);

  const distributeV = useCallback(() => {
    const items = getSelectedRects();
    if (items.length < 3) return;
    const sorted = [...items].sort((a, b) => a.rect.y - b.rect.y);
    const first = sorted[0].rect.y;
    const last = sorted[sorted.length - 1].rect.y;
    const step = (last - first) / (sorted.length - 1);
    onMoveFields(sorted.map((item, i) => ({ id: item.id, rect: { ...item.rect, y: first + step * i } })));
  }, [getSelectedRects, onMoveFields]);

  return useMemo(
    () => ({
      canAlign,
      alignLeft,
      alignRight,
      alignTop,
      alignBottom,
      alignCenterH,
      alignCenterV,
      distributeH,
      distributeV,
    }),
    [canAlign, alignLeft, alignRight, alignTop, alignBottom, alignCenterH, alignCenterV, distributeH, distributeV],
  );
}
